import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const sections = [
  { id: "privacy", label: "Privacy Policy" },
  { id: "terms", label: "Terms & Conditions" },
  { id: "cookies", label: "Cookie Policy" },
  { id: "disclaimer", label: "Disclaimer" },
];

const LAST_UPDATED = "25 February 2026";

function PrivacyPolicy() {
  return (
    <div>
      <h2
        className="text-3xl text-white font-light mb-2"
        style={ { fontFamily: "'Playfair Display', serif" }}
      >
        Privacy Policy
      </h2>
      <p className="text-gray-600 text-xs tracking-wide mb-10">Last updated: {LAST_UPDATED}</p>

      <div className="space-y-8 text-gray-400 text-sm font-light leading-relaxed">
        <div>
          <h3 className="text-white text-base font-light mb-3" style={ { fontFamily: "'Playfair Display', serif" }}>1. Who We Are</h3>
          <p>Raha-ídúù Experience Ltd ("Raha-ídúù", "we", "us", "our") is a luxury concierge and lifestyle management company registered in Nigeria. We are the data controller for information collected through our website at raha-iduu.ainaeco.uk and through our concierge services.</p>
        </div>
        <div>
          <h3 className="text-white text-base font-light mb-3" style={ { fontFamily: "'Playfair Display', serif" }}>2. Information We Collect</h3>
          <p className="mb-3">We may collect the following categories of personal information:</p>
          <ul className="space-y-2 ml-4">
            {["Identity information (name, title)", "Contact information (email address, phone number)", "Service preferences and request details you provide through enquiry forms", "Technical data (IP address, browser type, pages visited) collected automatically via standard server logs"].map(i => (
              <li key={i} className="flex items-start gap-2">
                <span className="w-1 h-1 bg-[#C4A77D] rounded-full mt-2 flex-shrink-0"></span>
                {i}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="text-white text-base font-light mb-3" style={ { fontFamily: "'Playfair Display', serif" }}>3. How We Use Your Information</h3>
          <p className="mb-3">We use your information to:</p>
          <ul className="space-y-2 ml-4">
            {["Respond to enquiries and deliver concierge services you have requested", "Communicate with you about your bookings and service arrangements", "Improve the functionality and content of our website", "Comply with applicable legal obligations"].map(i => (
              <li key={i} className="flex items-start gap-2">
                <span className="w-1 h-1 bg-[#C4A77D] rounded-full mt-2 flex-shrink-0"></span>
                {i}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="text-white text-base font-light mb-3" style={ { fontFamily: "'Playfair Display', serif" }}>4. Discretion & Confidentiality</h3>
          <p>Discretion is central to our service. We do not sell, rent, or otherwise disclose your personal information to third parties for marketing purposes. Information shared with third-party service providers (e.g., travel operators, venues) is limited strictly to what is necessary to fulfil your requested service, and only with providers who are bound by appropriate confidentiality obligations.</p>
        </div>
        <div>
          <h3 className="text-white text-base font-light mb-3" style={ { fontFamily: "'Playfair Display', serif" }}>5. Data Retention</h3>
          <p>We retain your personal information only for as long as necessary to fulfil the purposes for which it was collected, or as required by applicable Nigerian law.</p>
        </div>
        <div>
          <h3 className="text-white text-base font-light mb-3" style={ { fontFamily: "'Playfair Display', serif" }}>6. Your Rights</h3>
          <p>You have the right to request access to, correction of, or deletion of your personal data. To exercise any of these rights, please contact us at <a href="mailto:privacy@raha-iduu.com" className="text-[#C4A77D] hover:text-white transition-colors">privacy@raha-iduu.com</a>.</p>
        </div>
        <div>
          <h3 className="text-white text-base font-light mb-3" style={ { fontFamily: "'Playfair Display', serif" }}>7. Contact</h3>
          <p>For any privacy-related questions, please write to us at <a href="mailto:privacy@raha-iduu.com" className="text-[#C4A77D] hover:text-white transition-colors">privacy@raha-iduu.com</a>.</p>
        </div>
      </div>
    </div>
  );
}

function TermsAndConditions() {
  return (
    <div>
      <h2
        className="text-3xl text-white font-light mb-2"
        style={ { fontFamily: "'Playfair Display', serif" }}
      >
        Terms & Conditions
      </h2>
      <p className="text-gray-600 text-xs tracking-wide mb-10">Last updated: {LAST_UPDATED}</p>

      <div className="space-y-8 text-gray-400 text-sm font-light leading-relaxed">
        <div>
          <h3 className="text-white text-base font-light mb-3" style={ { fontFamily: "'Playfair Display', serif" }}>1. Acceptance of Terms</h3>
          <p>By accessing this website or engaging Raha-ídúù Experience Ltd for any service, you agree to be bound by these Terms & Conditions. If you do not agree with any part of these terms, please do not use our services.</p>
        </div>
        <div>
          <h3 className="text-white text-base font-light mb-3" style={ { fontFamily: "'Playfair Display', serif" }}>2. Services</h3>
          <p>Raha-ídúù provides bespoke concierge and lifestyle management services. The specific scope, pricing, and conditions of each engagement will be agreed individually between us and the client prior to commencement. All services are subject to availability and suitability at our discretion.</p>
        </div>
        <div>
          <h3 className="text-white text-base font-light mb-3" style={ { fontFamily: "'Playfair Display', serif" }}>3. Bookings & Payment</h3>
          <p>All bookings are confirmed only upon receipt of written confirmation from Raha-ídúù. Payment terms, deposit requirements, and cancellation policies will be clearly communicated at the time of booking. We reserve the right to decline any booking request at our absolute discretion.</p>
        </div>
        <div>
          <h3 className="text-white text-base font-light mb-3" style={ { fontFamily: "'Playfair Display', serif" }}>4. Cancellations & Refunds</h3>
          <p>Cancellation terms vary depending on the nature of the service and the third-party providers involved. Specific cancellation and refund policies will be stated in your individual service agreement. We will always endeavour to minimise any financial impact on our clients.</p>
        </div>
        <div>
          <h3 className="text-white text-base font-light mb-3" style={ { fontFamily: "'Playfair Display', serif" }}>5. Limitation of Liability</h3>
          <p>While we take every precaution in the selection of our service partners, Raha-ídúù acts as an intermediary for certain services. We shall not be liable for the acts or omissions of third-party providers. Our aggregate liability to any client shall not exceed the fees paid by that client in connection with the specific service in question.</p>
        </div>
        <div>
          <h3 className="text-white text-base font-light mb-3" style={ { fontFamily: "'Playfair Display', serif" }}>6. Governing Law</h3>
          <p>These Terms & Conditions are governed by and construed in accordance with the laws of the Federal Republic of Nigeria. Any disputes shall be subject to the exclusive jurisdiction of the Nigerian courts.</p>
        </div>
        <div>
          <h3 className="text-white text-base font-light mb-3" style={ { fontFamily: "'Playfair Display', serif" }}>7. Changes to Terms</h3>
          <p>We reserve the right to update these Terms & Conditions at any time. The date of the most recent revision is indicated at the top of this page. Continued use of our services following any changes constitutes acceptance of the revised terms.</p>
        </div>
      </div>
    </div>
  );
}

function CookiePolicy() {
  return (
    <div>
      <h2
        className="text-3xl text-white font-light mb-2"
        style={ { fontFamily: "'Playfair Display', serif" }}
      >
        Cookie Policy
      </h2>
      <p className="text-gray-600 text-xs tracking-wide mb-10">Last updated: {LAST_UPDATED}</p>

      <div className="space-y-8 text-gray-400 text-sm font-light leading-relaxed">
        <div>
          <h3 className="text-white text-base font-light mb-3" style={ { fontFamily: "'Playfair Display', serif" }}>What Are Cookies?</h3>
          <p>Cookies are small text files placed on your device when you visit a website. They are widely used to make websites work efficiently and to provide information to the website's operators.</p>
        </div>
        <div>
          <h3 className="text-white text-base font-light mb-3" style={ { fontFamily: "'Playfair Display', serif" }}>Cookies We Use</h3>
          <div className="space-y-4">
            {[
              { type: "Strictly Necessary", desc: "These cookies are essential for the website to function correctly. They cannot be disabled." },
              { type: "Performance & Analytics", desc: "We may use analytics to understand how visitors interact with our site, helping us improve the experience. No personally identifiable information is collected." },
              { type: "Functional", desc: "These cookies allow the website to remember your preferences and provide enhanced functionality." },
            ].map(({ type, desc }) => (
              <div key={type} className="p-5 border border-white/10 bg-white/[0.02]">
                <p className="text-white text-xs tracking-wide uppercase mb-2">{type}</p>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h3 className="text-white text-base font-light mb-3" style={ { fontFamily: "'Playfair Display', serif" }}>Managing Cookies</h3>
          <p>You can control and delete cookies through your browser settings. Note that disabling certain cookies may affect the functionality of the website. For more information on managing cookies, refer to your browser's help documentation.</p>
        </div>
        <div>
          <h3 className="text-white text-base font-light mb-3" style={ { fontFamily: "'Playfair Display', serif" }}>Contact</h3>
          <p>If you have any questions about our use of cookies, please contact us at <a href="mailto:privacy@raha-iduu.com" className="text-[#C4A77D] hover:text-white transition-colors">privacy@raha-iduu.com</a>.</p>
        </div>
      </div>
    </div>
  );
}

function Disclaimer() {
  return (
    <div>
      <h2
        className="text-3xl text-white font-light mb-2"
        style={ { fontFamily: "'Playfair Display', serif" }}
      >
        Disclaimer
      </h2>
      <p className="text-gray-600 text-xs tracking-wide mb-10">Last updated: {LAST_UPDATED}</p>

      <div className="space-y-8 text-gray-400 text-sm font-light leading-relaxed">
        <div>
          <h3 className="text-white text-base font-light mb-3" style={ { fontFamily: "'Playfair Display', serif" }}>Website Information</h3>
          <p>The information provided on this website is for general informational purposes only. While we endeavour to keep information accurate and current, we make no representations or warranties of any kind, express or implied, about the completeness, accuracy, reliability, or suitability of the information contained on this site.</p>
        </div>
        <div>
          <h3 className="text-white text-base font-light mb-3" style={ { fontFamily: "'Playfair Display', serif" }}>No Guarantee of Availability</h3>
          <p>The services, venues, and experiences referenced on this website are subject to availability. Descriptions of services or offerings do not constitute a guaranteed offer and are subject to change without notice.</p>
        </div>
        <div>
          <h3 className="text-white text-base font-light mb-3" style={ { fontFamily: "'Playfair Display', serif" }}>External Links</h3>
          <p>This website may contain links to external websites for your convenience. Raha-ídúù has no control over the content of those sites and accepts no responsibility for them or for any loss or damage that may arise from your use of them.</p>
        </div>
        <div>
          <h3 className="text-white text-base font-light mb-3" style={ { fontFamily: "'Playfair Display', serif" }}>Professional Advice</h3>
          <p>Nothing on this website constitutes legal, financial, or professional advice of any kind. You should seek independent professional advice before making any decisions based on information presented on this site.</p>
        </div>
        <div>
          <h3 className="text-white text-base font-light mb-3" style={ { fontFamily: "'Playfair Display', serif" }}>Contact</h3>
          <p>For any questions regarding this disclaimer, please contact us at <a href="mailto:info@raha-iduu.com" className="text-[#C4A77D] hover:text-white transition-colors">info@raha-iduu.com</a>.</p>
        </div>
      </div>
    </div>
  );
}

const contentMap: Record<string, JSX.Element> = {
  privacy: <PrivacyPolicy />,
  terms: <TermsAndConditions />,
  cookies: <CookiePolicy />,
  disclaimer: <Disclaimer />,
};

export default function LegalPage() {
  const location = useLocation();
  const [active, setActive] = useState("privacy");

  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (hash && sections.find((s) => s.id === hash)) {
      setActive(hash);
      setTimeout(() => {
        document.getElementById("legal-content")?.scrollIntoView({ behavior: "smooth" });
      }, 80);
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [location]);

  return (
    <main style={ { backgroundColor: "#0b1a14" }}>
      {/* Header */}
      <header className="relative pt-40 pb-20 px-6 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <span className="text-[#C4A77D] text-xs tracking-[0.4em] uppercase">
            Legal
          </span>
          <h1
            className="text-5xl md:text-6xl text-white font-light mt-4 leading-tight"
            style={ { fontFamily: "'Playfair Display', serif" }}
          >
            Legal Information
          </h1>
        </div>
      </header>

      {/* Content */}
      <div id="legal-content" className="max-w-4xl mx-auto px-6 py-16 flex flex-col md:flex-row gap-12">
        {/* Sidebar nav */}
        <nav className="md:w-52 flex-shrink-0">
          <p className="text-[#C4A77D] text-xs tracking-[0.3em] uppercase mb-5">Documents</p>
          <ul className="space-y-1 md:sticky md:top-28">
            {sections.map((s) => (
              <li key={s.id}>
                <button
                  onClick={() => setActive(s.id)}
                  className={`w-full text-left text-sm font-light py-2.5 px-3 transition-all duration-200 border-l-2 ${
                    active === s.id
                      ? "border-[#C4A77D] text-white bg-white/[0.04]"
                      : "border-transparent text-gray-500 hover:text-gray-300 hover:border-white/20"
                  }`}
                >
                  {s.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Document content */}
        <div className="flex-1 min-w-0">
          {contentMap[active]}
        </div>
      </div>
    </main>
  );
}
